import React, { useEffect, useRef, useState } from "react";
import "./ArticleEditor.css";
import Constants from "../../services/constants";

const formatDateForInput = (value) => {
  const date = value instanceof Date ? value : new Date(value || Date.now());

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const emptyArticle = {
  id: null,
  slug: "",
  category: Constants.BLOG_CATEGORIES.ARTICLES,
  title: "",
  description: "",
  date: formatDateForInput(new Date()),
  readTime: "2 min read",
  image: "",
  color: "pink",
  featured: false,
  readText: [""],
  draft: false,
};

const ArticleEditor = ({ article = null, setArticleData, onSave, onCancel }) => {
  const [formData, setFormData] = useState(emptyArticle);
  const [activeTab, setActiveTab] = useState("editor");
  const fileInputRef = useRef(null);

  // Treat edit-mode as true only when an existing article with an id is passed
  const isEditMode = Boolean(article && (article.id || article._id));

  useEffect(() => {
    if (article) {
      setFormData({
        ...emptyArticle,
        ...article,
        date: article.date ? formatDateForInput(article.date) : formatDateForInput(new Date()),
        readText:
          Array.isArray(article.readText) && article.readText.length
            ? article.readText
            : [""],
      });
    } else {
      setFormData({ ...emptyArticle, date: formatDateForInput(new Date()) });
    }
  }, [article]);

  // ----------------------------------------
  // Generic input handler
  // ----------------------------------------
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setArticleData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select an image file.");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    const MAX_FILE_SIZE = 5 * 1024 * 1024;

    if (file.size > MAX_FILE_SIZE) {
      alert("Please select an image smaller than 5MB.");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      setFormData((prev) => ({
        ...prev,
        image: reader.result,
      }));
      setArticleData((prev) => ({
        ...prev,
        image: reader.result,
      }));
    };

    reader.readAsDataURL(file);
  };

  // ----------------------------------------
  // Auto generate slug (robust, Unicode-aware)
  // ----------------------------------------
  const generateSlug = (title) => {
    if (!title) return "";
    return title
      .toString()
      .normalize("NFKD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;

    setFormData((prev) => {
      const shouldGenerate = !isEditMode || !prev.slug;
      const updated = {
        ...prev,
        title,
        slug: shouldGenerate ? generateSlug(title) : prev.slug,
      };

      if (setArticleData) setArticleData(updated);

      return updated;
    });
  };

  // ----------------------------------------
  // readText handlers
  // ----------------------------------------
  const handleReadTextChange = (index, value) => {
    setFormData((prev) => {
      const updated = [...prev.readText];
      updated[index] = value;

      return {
        ...prev,
        readText: updated,
      };
    });

    setArticleData((prev) => {
      const updated = [...prev.readText];
      updated[index] = value;

      return {
        ...prev,
        readText: updated,
      };
    });
  };

  const addContentBlock = () => {
    setFormData((prev) => ({
      ...prev,
      readText: [...prev.readText, ""],
    }));

    setArticleData((prev) => ({
      ...prev,
      readText: [...prev.readText, ""],
    }));
  };

  const removeContentBlock = (index) => {
    setFormData((prev) => {
      if (prev.readText.length === 1) {
        return {
          ...prev,
          readText: [""],
        };
      }

      return {
        ...prev,
        readText: prev.readText.filter((_, i) => i !== index),
      };
    });

    setArticleData((prev) => {
      if (prev.readText.length === 1) {
        return {
          ...prev,
          readText: [""],
        };
      }

      return {
        ...prev,
        readText: prev.readText.filter((_, i) => i !== index),
      };
    });

  };

  const moveContentBlock = (index, direction) => {
    setFormData((prev) => {
      const updated = [...prev.readText];

      const newIndex = direction === "up" ? index - 1 : index + 1;

      if (newIndex < 0 || newIndex >= updated.length) {
        return prev;
      }

      const temp = updated[index];
      updated[index] = updated[newIndex];
      updated[newIndex] = temp;

      return {
        ...prev,
        readText: updated,
      };
    });

    setArticleData((prev) => {
      const updated = [...prev.readText];

      const newIndex = direction === "up" ? index - 1 : index + 1;

      if (newIndex < 0 || newIndex >= updated.length) {
        return prev;
      }

      const temp = updated[index];
      updated[index] = updated[newIndex];
      updated[newIndex] = temp;

      return {
        ...prev,
        readText: updated,
      };
    });
  };

  // ----------------------------------------
  // Validation
  // ----------------------------------------
  const validateForm = () => {
    if (!formData.title.trim()) {
      alert("Please enter article title.");
      return false;
    }

    if (!formData.slug.trim()) {
      alert("Please enter article slug.");
      return false;
    }

    if (!formData.category.trim()) {
      alert("Please enter article category.");
      return false;
    }

    //removed desc as required
    // if (!formData.description.trim()) {
    //   alert("Please enter article description.");
    //   return false;
    // }

    return true;
  };

  // ----------------------------------------
  // Save
  // ----------------------------------------
  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const articleToSave = {
      ...formData,

      // Remove empty HTML blocks
      readText: formData.readText.filter((item) => item.trim() !== ""),
    };

    onSave(articleToSave);
  };

  // Called by header save button so it works from preview mode
  const handleSaveClick = (e) => {
    // directly run submit logic; pass through event if present
    handleSubmit(e);
  };

  // ----------------------------------------
  // Preview
  // ----------------------------------------
  const renderPreview = () => {
    return (
      <div className="article-preview">
        <div className="article-preview-card">

          {formData.image && (
            <div className="article-preview-image-wrapper">
              <img
                src={formData.image}
                alt={formData.title}
                className="article-preview-image"
              />
            </div>
          )}

          <div className="article-preview-content">

            <div className="article-preview-meta">
              <span
                className={`article-category ${formData.color}`}
              >
                {formData.category}
              </span>

              {formData.featured && (
                <span className="featured-badge">
                  FEATURED
                </span>
              )}
            </div>

            <h1>{formData.title || "Article title"}</h1>

            <p className="article-preview-description">
              {formData.description || "Article description"}
            </p>

            <div className="article-preview-info">
              <span>
                {formData.date || new Date().toLocaleDateString()}
              </span>

              <span>•</span>

              <span>
                {formData.readTime || "2 min read"}
              </span>
            </div>

            <div className="article-preview-body">
              {formData.readText.map((html, index) => {
                if (!html.trim()) return null;

                return (
                  <div
                    key={index}
                    dangerouslySetInnerHTML={{
                      __html: html,
                    }}
                  />
                );
              })}
            </div>

          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="article-editor">

      {/* -------------------------------- */}
      {/* Tabs */}
      {/* -------------------------------- */}
      <div className="article-editor-tabs">

        <button
          type="button"
          className={
            activeTab === "editor"
              ? "active"
              : ""
          }
          onClick={() => setActiveTab("editor")}
        >
          Editor
        </button>

        <button
          type="button"
          className={
            activeTab === "preview"
              ? "active"
              : ""
          }
          onClick={() => setActiveTab("preview")}
        >
          Preview
        </button>

      </div>
      {/* -------------------------------- */}
      {/* Header */}
      {/* -------------------------------- */}
      <div className="article-editor-header">

        <div>
          <h2>
            {isEditMode ? "Edit Article" : "Add Article"}
          </h2>

          <p>
            {isEditMode
              ? "Update the article details and content."
              : "Create a new article."}
          </p>
        </div>

        <div className="article-editor-actions">

          {onCancel && (
            <button
              type="button"
              className="article-btn article-btn-secondary"
              onClick={onCancel}
            >
              Cancel
            </button>
          )}

          <button
            type="button"
            className="article-btn article-btn-pink"
            onClick={handleSaveClick}
          >
            {isEditMode ? "Update Article" : "Create Article"}
          </button>

        </div>

      </div>


      {activeTab === "editor" ? (

        <form
          id="article-editor-form"
          onSubmit={handleSubmit}
          className="article-editor-form"
        >

          {/* -------------------------------- */}
          {/* Basic Information */}
          {/* -------------------------------- */}

          <section className="article-editor-section">

            <div className="section-heading">
              <h3>Basic Information</h3>
              <p>Information displayed on the article card.</p>
            </div>

            <div className="article-form-grid">

              <div className="article-form-group article-form-full">
                <label>Title *</label>

                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleTitleChange}
                  placeholder="The hottest TikTok trends right now"
                />
              </div>

              <div className="article-form-group">
                <label>Slug *</label>

                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  placeholder="hottest-tiktok-trends-august-2026"
                />

                <small>
                  Used in the article URL.
                </small>
              </div>

              <div className="article-form-group">
                <label>Category *</label>

                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                >
                  <option value={Constants.BLOG_CATEGORIES.ARTICLES}>Articles</option>
                  <option value={Constants.BLOG_CATEGORIES.TUTORIALS}>Tutorials</option>
                  <option value={Constants.BLOG_CATEGORIES.NEWS}>News</option>
                  <option value={Constants.BLOG_CATEGORIES.REVIEWS}>Reviews</option>
                </select>
              </div>
                {/* <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  placeholder="TIKTOK ARTICLES"
                />
              </div> */}

              <div className="article-form-group article-form-full">
                <label>Description</label>

                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Short description of the article..."
                />
              </div>

              <div className="article-form-group">
                <label>Date</label>

                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  placeholder="Aug 26, 2026"
                />
              </div>

              <div className="article-form-group">
                <label>Read Time</label>

                <input
                  type="text"
                  name="readTime"
                  value={formData.readTime}
                  onChange={handleChange}
                  placeholder="2 min read"
                />
              </div>

            </div>

          </section>

          {/* -------------------------------- */}
          {/* Appearance */}
          {/* -------------------------------- */}

          <section className="article-editor-section">

            <div className="section-heading">
              <h3>Appearance</h3>
              <p>Configure the article card appearance.</p>
            </div>

            <div className="article-form-grid">

              <div className="article-form-group article-form-full">
                <label>Image</label>

                <div className="image-picker-row">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="article-image-file-picker"
                    onChange={handleImageFileChange}
                  />

                  {formData.image && (
                    <button
                      type="button"
                      className="article-btn article-btn-secondary image-clear-btn"
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, image: "" }));
                        if (fileInputRef.current) {
                          fileInputRef.current.value = "";
                        }
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>

                <small className="image-upload-hint">
                  Upload an image file or paste a direct image URL. Max size: 5MB.
                </small>

                {formData.image && (
                  <>
                    <small className="image-value-label">
                      {formData.image.startsWith("data:image/")
                        ? "Selected file preview"
                        : `Current image URL: ${formData.image}`}
                    </small>

                    <div className="image-preview-small">
                      <img
                        src={formData.image}
                        alt="Preview"
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="article-form-group">
                <label>Color</label>

                <select
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                >
                  <option value="orange">Orange</option>
                  <option value="blue">Blue</option>
                  <option value="green">Green</option>
                  <option value="purple">Purple</option>
                  <option value="pink">Pink</option>
                  <option value="red">Red</option>
                  <option value="yellow">Yellow</option>
                </select>
              </div>

              <div className="article-form-group checkbox-group">

                <label className="checkbox-label">

                  <input
                    type="checkbox"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleChange}
                  />

                  <span>
                    Featured Article
                  </span>

                </label>

                <small>
                  Display this article as a featured article.
                </small>

              </div>

              <div className="article-form-group checkbox-group">

                <label className="checkbox-label">

                  <input
                    type="checkbox"
                    name="draft"
                    checked={formData.draft}
                    onChange={handleChange}
                  />

                  <span>
                    Draft Article
                  </span>

                </label>

                <small>
                  Save as Draft.
                </small>

              </div>

            </div>

          </section>

          {/* -------------------------------- */}
          {/* Article Content */}
          {/* -------------------------------- */}

          <section className="article-editor-section">

            <div className="section-heading">

              <div>
                <h3>Article Content</h3>

                <p>
                  Add HTML content blocks to build the article.
                </p>
              </div>

              <button
                type="button"
                className="article-btn article-btn-secondary"
                onClick={addContentBlock}
              >
                + Add Content
              </button>

            </div>

            <div className="content-blocks">

              {formData.readText.map((content, index) => (

                <div
                  className="content-block"
                  key={index}
                >

                  <div className="content-block-header">

                    <div className="content-block-title">
                      Content Block {index + 1}
                    </div>

                    <div className="content-block-actions">

                      <button
                        type="button"
                        onClick={() =>
                          moveContentBlock(index, "up")
                        }
                        disabled={index === 0}
                        title="Move up"
                      >
                        ↑
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          moveContentBlock(index, "down")
                        }
                        disabled={
                          index ===
                          formData.readText.length - 1
                        }
                        title="Move down"
                      >
                        ↓
                      </button>

                      <button
                        type="button"
                        className="delete"
                        onClick={() =>
                          removeContentBlock(index)
                        }
                        title="Delete"
                      >
                        ×
                      </button>

                    </div>

                  </div>

                  <textarea
                    value={content}
                    onChange={(e) =>
                      handleReadTextChange(
                        index,
                        e.target.value
                      )
                    }
                    placeholder="<p>Write your article paragraph...</p>"
                    rows="6"
                  />

                  {content.trim() && (
                    <div className="content-live-preview">

                      <div className="preview-label">
                        Preview
                      </div>

                      <div
                        dangerouslySetInnerHTML={{
                          __html: content,
                        }}
                      />

                    </div>
                  )}

                </div>

              ))}

            </div>

          </section>

          {/* -------------------------------- */}
          {/* Bottom Actions */}
          {/* -------------------------------- */}

          <div className="article-editor-footer">

            {onCancel && (
              <button
                type="button"
                className="article-btn article-btn-secondary"
                onClick={onCancel}
              >
                Cancel
              </button>
            )}

            <button
              type="submit"
              className="article-btn article-btn-pink"
              form="article-editor-form"
            >
              {isEditMode
                ? "Update Article"
                : "Create Article"}
            </button>

          </div>

        </form>

      ) : (

        <div className="article-preview-container">
          {renderPreview()}
        </div>

      )}

    </div>
  );
};

export default ArticleEditor;